import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  APP_CONTAINER_ROUTES,
  APP_ROUTES,
  ORDER_ROUTES,
} from '@/navigators/routes';
import {
  AppContainerStackParams,
  AppStackParams,
  OrdersStackParams,
} from '@/navigators/types';
import { CompositeNavigationProp, RouteProp } from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { InteractionManager, Text, View } from 'react-native';
import { OrderHistoryTab } from '@/components/orders/order-history-tab';
import { PageContainer } from '@/components/page-container';
import { StackNavigationProp } from '@react-navigation/stack';
import { SubscriptionButton } from '@/components/subscription-button';
import { TabView, Props as TabViewProps } from '@/components/tab-view';
import { TYPOGRAPHY } from '@/styles';
import { useHeaderRestaurantSelectorProps } from '@/components/use-header-restaurant-selector-props';
import { ViewOrderButton } from '@/components/partners/view-order-button';

enum OrderTabRoutes {
  Past = 'Past',
  Upcoming = 'Upcoming',
}

export interface TabRoute {
  key: OrderTabRoutes;
  title: string;
}

export interface Props {
  navigation: CompositeNavigationProp<
    StackNavigationProp<OrdersStackParams, ORDER_ROUTES.Orders>,
    CompositeNavigationProp<
      StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES>,
      StackNavigationProp<AppStackParams>
    >
  >;
  route: RouteProp<OrdersStackParams, ORDER_ROUTES.Orders>;
}

export const OrdersScreen: React.FC<Props> = ({
  navigation,
  route: screenRoute,
}) => {
  const baseHeaderProps = useHeaderRestaurantSelectorProps();
  const [styles, theme] = useStyles();

  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    const preselectedOrderUUID = screenRoute.params?.orderUUID;
    if (preselectedOrderUUID) {
      InteractionManager.runAfterInteractions(() => {
        navigation.navigate(APP_CONTAINER_ROUTES.OrderDetail, {
          orderUUID: preselectedOrderUUID,
        });
      });
    }
  }, [screenRoute.params?.orderUUID, navigation]);

  const onNewOrderPress = useCallback(() => {
    // Navigate to order guide tab for whitelabel apps
    return navigation.navigate(APP_ROUTES.OrderGuide);
  }, [navigation]);

  const onCreditRequestSelect = useCallback(
    (creditRequestBatchUUID: string) => {
      navigation.navigate(APP_CONTAINER_ROUTES.CreditRequestDetail, {
        creditRequestBatchUUID,
      });
    },
    [navigation],
  );

  const onInvoiceSelect = useCallback(
    (invoiceUUID: string) => {
      navigation.navigate(APP_CONTAINER_ROUTES.InvoiceDetail, {
        invoiceUUID,
        renderPayable: false,
      });
    },
    [navigation],
  );

  const onOrderSelect = useCallback(
    (orderUUID: string) => {
      navigation.navigate(APP_CONTAINER_ROUTES.OrderDetail, {
        orderUUID,
      });
    },
    [navigation],
  );

  const navigationState = useMemo(
    () => ({
      index: tabIndex,
      routes: [
        {
          key: OrderTabRoutes.Upcoming,
          title: i18n.t('upcoming'),
        },
        {
          key: OrderTabRoutes.Past,
          title: i18n.t('past'),
        },
      ],
    }),
    [tabIndex],
  );

  const renderScene: TabViewProps<TabRoute>['renderScene'] = useCallback(
    ({ route }) => (
      <View style={styles.orderTabContainer}>
        <OrderHistoryTab
          onCreditRequestSelect={onCreditRequestSelect}
          onInvoiceSelect={onInvoiceSelect}
          onNewOrderPress={onNewOrderPress}
          onOrderSelect={onOrderSelect}
          showPastOrders={route.key === OrderTabRoutes.Past}
        />
      </View>
    ),
    [
      onCreditRequestSelect,
      onInvoiceSelect,
      onNewOrderPress,
      onOrderSelect,
      styles.orderTabContainer,
    ],
  );

  const renderTabBarLabel: TabViewProps<TabRoute>['label'] = useCallback(
    ({ color, route }: { color: string; route: TabRoute }) => {
      return (
        <Text style={[TYPOGRAPHY.tabBarLabel, { color }]} testID={route.title}>
          {route.title}
        </Text>
      );
    },
    [],
  );

  return (
    <PageContainer
      navigationHeaderProps={{
        ...baseHeaderProps,
        actions: [
          {
            component: <SubscriptionButton />,
          },
        ],
        showShadow: false,
        title: i18n.t('orders'),
      }}
      statusBarColor={theme.colors.background}
    >
      <TabView<TabRoute>
        label={renderTabBarLabel}
        lazy
        navigationState={navigationState}
        onIndexChange={setTabIndex}
        renderScene={renderScene}
        tabBarTabStyle={styles.tabBarTab}
      />
      <ViewOrderButton />
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  orderTabContainer: {
    backgroundColor: theme.colors.background,
    flex: 1,
  },
  tabBarTab: {
    paddingTop: 4,
  },
}));
